import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { readRouteParam } from '../../../_utils/route-param.utils';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import IUser from '../../../_interfaces/IUser';
import { AdminEntityLoaderService } from '../../../_services/admin/admin-entity-loader.service';
import { UsersService } from '../../../_services/users/users.service';
import { SessionService } from '../../../_services/session/session.service';
import { AdminFormLayoutComponent } from '../../../components/admin-ui/admin-form-layout/admin-form-layout.component';
import { AdminStatePanelComponent } from '../../../components/admin-ui/admin-state-panel/admin-state-panel.component';
import { FormUserAccessComponent } from '../../../components/forms/form-user-access/form-user-access.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-admin-user-access',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    AdminFormLayoutComponent,
    AdminStatePanelComponent,
    FormUserAccessComponent,
  ],
  templateUrl: './admin-user-access.component.html',
  styleUrl: './admin-user-access.component.scss',
})
export class AdminUserAccessComponent implements OnInit {
  public customersId = 0;
  public usersId = 0;
  public user: IUser | null = null;
  public loading = true;
  public hasError = false;
  private currentUserId = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly loader: AdminEntityLoaderService,
    private readonly usersService: UsersService,
    private readonly sessionService: SessionService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.currentUserId = +(this.sessionService.getUserID() || 0);
    this.customersId = readRouteParam(this.route, 'customers_id');
    this.usersId = readRouteParam(this.route, 'users_id');
    this.load(true);
  }

  get isOwnUser(): boolean {
    return !!this.user?.id && +this.user.id === +this.currentUserId;
  }

  get isPrimaryAdmin(): boolean {
    return !!this.user?.isPrimaryAdmin;
  }

  get userDisplayName(): string {
    const name = [this.user?.name, this.user?.surname].filter(Boolean).join(' ').trim();
    return name || this.user?.email || '';
  }

  get adminStatusLabelKey(): string {
    return this.user?.isAdmin
      ? 'ADMIN.USERS.ADMIN_STATUS_ENABLED'
      : 'ADMIN.USERS.ADMIN_STATUS_DISABLED';
  }

  get adminActionLabelKey(): string {
    return this.user?.isAdmin
      ? 'ADMIN.USERS.ADMIN_REVOKE'
      : 'ADMIN.USERS.ADMIN_MAKE';
  }

  load(fromResolver = false): void {
    if (!this.customersId || !this.usersId) {
      this.hasError = true;
      this.loading = false;
      return;
    }

    this.loading = true;
    this.hasError = false;
    let hasResolverData = false;

    if (fromResolver) {
      const user = (this.route.parent?.snapshot.data['user'] || null) as
        | IUser
        | null;
      if (user) {
        this.user = user;
        this.loading = false;
        hasResolverData = true;
      }
    }

    this.loader.loadUser(this.customersId, this.usersId).subscribe({
      next: (user) => {
        this.user = user;
        this.hasError = !user;
        this.loading = false;
      },
      error: () => {
        if (hasResolverData) {
          return;
        }
        this.hasError = true;
        this.loading = false;
      },
    });
  }

  confirmToggleAdmin(): void {
    if (!this.user?.id || this.isOwnUser || this.isPrimaryAdmin) {
      return;
    }

    const isAdmin = this.user.isAdmin ? 0 : 1;
    const header = isAdmin
      ? 'ADMIN.USERS.CONFIRM.ADMIN.MAKE.TITLE'
      : 'ADMIN.USERS.CONFIRM.ADMIN.REVOKE.TITLE';
    const message = isAdmin
      ? 'ADMIN.USERS.CONFIRM.ADMIN.MAKE.TEXT'
      : 'ADMIN.USERS.CONFIRM.ADMIN.REVOKE.TEXT';

    this.confirmationService.confirm({
      header: this.translate.instant(header, { user: this.userDisplayName }),
      message: this.translate.instant(message, { user: this.userDisplayName }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.toggleAdmin(isAdmin),
    });
  }

  private toggleAdmin(isAdmin: 0 | 1): void {
    if (!this.user?.id) {
      return;
    }
    this.usersService
      .updateAdminAccess(this.user.id, this.customersId, isAdmin)
      .subscribe({
        next: () => {
          if (this.user) {
            this.user = { ...this.user, isAdmin: isAdmin === 1 };
          }
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant(
              isAdmin
                ? 'MESSAGES.USERS.ADMIN.MAKE.SUMMARY'
                : 'MESSAGES.USERS.ADMIN.REVOKE.SUMMARY',
            ),
            detail: this.translate.instant(
              isAdmin
                ? 'MESSAGES.USERS.ADMIN.MAKE.DETAIL'
                : 'MESSAGES.USERS.ADMIN.REVOKE.DETAIL',
              {
                user: this.userDisplayName,
              },
            ),
          });
          this.load(false);
        },
      });
  }
}
